import React from 'react';

import {BrowseCourse} from '@udemy/browse-course';
import {Academy, Article, buildInDemandCareer, InDemandCareer} from '@udemy/career-academies';
import {AboveTheFoldProvider} from '@udemy/design-system-utils';
import {LearningTestimonial} from '@udemy/react-discovery-units';

import {useVerifyIsUsMarketplace} from '../../hooks/useVerifyIsUsMarketplace';
import {AcademiesCourseExploration} from './components/academies-course-exploration/academies-course-exploration.react-component';
import {FrequentlyAskedQuestions} from './components/academies-faq/academies-faq.react-component';
import {AcademiesStepWrapper} from './components/academies-steps/academies-step-wrapper.react-component';
import {AdditionalResources} from './components/additional-resources/additional-resources.react-component';
import {CalpHero} from './components/calp-hero/calp-hero.react-component';
import {Certificate} from './components/certificate/certificate.react-component';
import {AcademyCurriculumContainer} from './components/curriculum/academy-curriculum-container.react-component';
import {InDemandCareers} from './components/in-demand-careers/in-demand-careers.react-component';
import {PackageOverview} from './components/package-overview/package-overview.react-component';
import {Testimonials} from './components/testimonials/testimonials.react-component';
import {TopicLinks} from './components/topic-links/topic-links.react-component';
import {Topic} from './components/topic-links/types';

export interface CareerAcademyLandingPageProps {
    academy: Academy;
    courses?: BrowseCourse[];
    topics?: Topic[];
    locale: string;
    inDemandAcademiesList: InDemandCareer[];
}

export function CareerAcademyLandingPage({
    academy,
    courses,
    topics,
    locale,
    inDemandAcademiesList,
}: CareerAcademyLandingPageProps) {
    const inDemandCareer = buildInDemandCareer(academy, courses);
    const {isUSMarketplace, isFallbackCountryUS} = useVerifyIsUsMarketplace(locale);
    return (
        <div>
            <AboveTheFoldProvider>
                <CalpHero
                    academy={inDemandCareer}
                    courses={courses}
                    isUSMarketplace={isUSMarketplace}
                    showJobStatsLoader={isFallbackCountryUS}
                />

                <AcademiesStepWrapper
                    slug={inDemandCareer.slug}
                    summariesOrder={inDemandCareer.curriculum.buyableIds}
                    summariesContent={inDemandCareer.curriculum.summaries}
                />
            </AboveTheFoldProvider>

            <TopicLinks topics={topics} />

            <PackageOverview academy={inDemandCareer} courses={courses} />

            <AcademyCurriculumContainer academy={inDemandCareer} courses={courses} />

            <Certificate
                academy={inDemandCareer}
                courses={courses}
                isUSMarketplace={isUSMarketplace}
            />

            <Testimonials testimonials={inDemandCareer.testimonials as LearningTestimonial[]} />

            <AdditionalResources articles={academy.articles as Article[]} career={academy.career} />

            <FrequentlyAskedQuestions questionsAndAnswers={academy.frequentlyAskedQuestions} />

            {/* Setting isUSMarketplace to true for MVP purposes */}
            <InDemandCareers
                academies={inDemandAcademiesList}
                isUSMarketplace={isUSMarketplace}
                uiRegion={'career_accelerator_landing_page_bottom'}
            />

            <AcademiesCourseExploration academy={inDemandCareer} topics={topics} />
        </div>
    );
}
